import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  CheckboxGroup,
  Flex,
  VStack,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";

export interface CheckboxItem {
  label: string;
  children: {
    idx: string;
    label: string;
  }[];
}

interface CheckBoxTreeProps {
  treeTitle?: string | "CheckBoxTree";
  records: Record<string, unknown>[] | [];
  fieldName: string | "";
  filteredData: boolean;
  initialValues: string[];
  selectedItems: Map<string, string[]>;
  setSelectedItems: React.Dispatch<React.SetStateAction<Map<string, string[]>>>;
}

const CheckboxTreeItem = ({
  item,
  onChange,
  initialValues,
  localSelectedItems,
}: {
  item: CheckboxItem;
  onChange?: (v: string[]) => void;
  initialValues?: string[];
  localSelectedItems: Record<string, string[]>;
}) => {
  const hasChildren = item.children && item.children.length > 0;

  const [checkedItemsIdx, setCheckedItemsIdx] = useState<string[]>(
    localSelectedItems?.[item.label]?.length
      ? localSelectedItems?.[item.label]
      : item.children
          .filter((child) => initialValues?.includes(child.idx))
          .map((child) => child.idx),
  );

  const allChecked = checkedItemsIdx.length === item.children.length;
  const isIndeterminate = checkedItemsIdx.length > 0 && !allChecked;

  const handleOnChange = (checked: boolean, itemIdx: string) => {
    if (checked) {
      setCheckedItemsIdx((prev) => [...prev, itemIdx]);
    } else {
      setCheckedItemsIdx((prev) => prev.filter((idx) => idx !== itemIdx));
    }
  };

  useEffect(() => {
    const updatedInitialValues = checkedItemsIdx.filter((idx) =>
      item.children.some((child) => child.idx === idx),
    );
    onChange?.(updatedInitialValues);
  }, [checkedItemsIdx]);

  return (
    <AccordionItem>
      <AccordionButton>
        <Flex justifyContent="space-between" w="full">
          <Box>
            <Checkbox
              isChecked={allChecked}
              isIndeterminate={isIndeterminate}
              onChange={() => {
                if (allChecked) {
                  setCheckedItemsIdx([]);
                } else {
                  const val = item.children.map((i) => i.idx);
                  setCheckedItemsIdx(val);
                }
              }}
            >
              {item.label}
            </Checkbox>
          </Box>
          <AccordionIcon />
        </Flex>
      </AccordionButton>
      <AccordionPanel>
        <VStack align="start" spacing={2} ml={4}>
          {hasChildren &&
            item.children.map((child) => {
              const isChecked = checkedItemsIdx.includes(child.idx);

              return (
                <Checkbox
                  key={child.idx}
                  isChecked={isChecked}
                  onChange={(e) => handleOnChange(e.target.checked, child.idx)}
                >
                  {(child.label == "get" && "READ") ||
                    (child.label == "post" && "WRITE") ||
                    (child.label == "put" && "OVERWRITE") ||
                    (child.label == "patch" && "UPDATE") ||
                    (child.label == "delete" && "DELETE")}
                </Checkbox>
              );
            })}
        </VStack>
      </AccordionPanel>
    </AccordionItem>
  );
};

export const CheckBoxTree: React.ComponentType<CheckBoxTreeProps> = ({
  records,
  fieldName,
  filteredData,
  selectedItems,
  initialValues,
  setSelectedItems,
  ...props
}: CheckBoxTreeProps) => {
  const { setFieldValue } = useFormikContext();
  const [localSelectedItems, setLocalSelectedItems] = useState<
    Record<string, string[]>
  >(
    selectedItems && typeof selectedItems[Symbol.iterator] === "function"
      ? Object.fromEntries(selectedItems)
      : {},
  );

  const [visibleData, setVisibleData] = useState<CheckboxItem[]>(
    records as CheckboxItem[],
  );

  useEffect(() => {
    if (records) {
      if (filteredData) {
        setVisibleData(records as CheckboxItem[]);
      } else {
        const newData = records.filter((data) => {
          return !visibleData.some(
            (existingData) => existingData.label === data.label,
          );
        });
        const newDataList = Array.from(new Set([...visibleData, ...newData]));
        setVisibleData(newDataList as CheckboxItem[]);
      }
    }
  }, [records, filteredData]);

  useEffect(() => {
    const mapToRecord =
      selectedItems instanceof Map
        ? Object.fromEntries(selectedItems)
        : selectedItems;
    // Check if selectedItems is iterable (array or Map) before updating localSelectedItems
    setLocalSelectedItems(mapToRecord);
  }, [selectedItems]);

  useEffect(() => {
    const selectedItemsArray = Array.from(Object.values(localSelectedItems));
    const consolidatedValues = selectedItemsArray.flat();
    if (consolidatedValues.length > 0) {
      setFieldValue(fieldName, consolidatedValues);
    }
  }, [setFieldValue, localSelectedItems, fieldName]);
  // const recordFromMap = Object.fromEntries(localSelectedItems)
  return (
    <CheckboxGroup>
      {visibleData &&
        visibleData.map((item) => (
          <Accordion
            allowToggle
            minW="full"
            className="accordion-item"
            key={item.label}
          >
            <CheckboxTreeItem
              {...props}
              item={item}
              onChange={(items) => {
                setLocalSelectedItems((selected) => ({
                  ...selected,
                  ...{ [item.label]: items },
                }));
                setSelectedItems((selected) => ({
                  ...selected,
                  ...{ [item.label]: items },
                }));
              }}
              initialValues={initialValues}
              localSelectedItems={localSelectedItems}
            />
          </Accordion>
        ))}
    </CheckboxGroup>
  );
};

export default CheckBoxTree;
